import { useTranslation } from 'react-i18next'

import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import {
  Checkbox,
  Field,
  Select,
  Textarea} from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { useRoles } from '@/features/settings/api/useRoles'
import { roleLabel } from '@/lib/navigation'
import { Input } from '@/components/ui/Input'
import { ContactsEditor } from '@/features/directories/components/ContactsEditor'
import {
  contactToPayload,
  type ContactPayload,
} from '@/features/directories/api/useDirectory'
import type {
  Client,
  DirectoryParticipant,
  DirectoryUser,
  Grants,
  Office,
  ParticipantRole,
  Role,
  TransportRef,
} from '@/types/domain'

/* The four directory editors.
 *
 * Each is the same shape — a FormSheet, a draft in local state, one required
 * field — and they stay four components rather than one generic one because
 * the FIELDS differ in kind, not just in label: a user has a role and an active
 * flag, a participant has a WeChat handle, a transport ref has a code. A single
 * schema-driven form would need a config language richer than the four forms it
 * replaced.
 *
 * `record` present means edit, absent means add. That one prop is what makes
 * these serve both actions: the surfaces below never mount two sheets.
 *
 * Suppressed values (AD-6) are never loaded into an editable input — a Sales
 * user editing a client must not have "Contact restricted" written back over a
 * real contact. The field renders empty and disabled instead.
 */

/* Suppression is handled in two places, both deliberate: contactToPayload
 * turns a withheld value into an empty string on the way into a draft, and
 * ContactsEditor disables the input. Either alone would be enough today; both
 * means a re-enabled input still cannot write the marker back over a real
 * number (AD-6). */

/* The seven types are NOT listed here any more. Which of them a caller may
 * create is a permission decision (FR-40) — Sales gets five, Pricing gets all
 * seven — so the sheet takes them as a prop rather than offering options the
 * server will refuse. */

/* The role picker is fed from the SERVER, not from a list here.
 *
 * Roles are runtime-definable since migration 0021, so a hardcoded five would
 * mean a founder could create a role and then never assign anybody to it —
 * the feature would be half-built, and the half that was missing is the half
 * that makes it useful.
 *
 * The seeded five keep their translations; a role somebody created shows the
 * name they typed (see roleLabel). */

/* ── Client ───────────────────────────────────────────────────────────────── */

export interface ClientFormSheetProps {
  open: boolean
  onClose: () => void
  /** Absent for a new record. */
  record?: Client
  onSubmit: (values: ClientDraft) => void
  pending?: boolean
  /** False when the caller's role may not read contact details (NFR-3). */
  canEditContacts?: boolean
}

export interface ClientDraft {
  name: string
  code: string
  taxId: string
  registrationId: string
  country: string
  address: string
  contacts: ContactPayload[]
}

export function ClientFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  canEditContacts = true,
}: ClientFormSheetProps) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<ClientDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            code: record.code ?? '',
            taxId: record.taxId ?? '',
            registrationId: record.registrationId ?? '',
            country: record.country,
            address: record.address ?? '',
            contacts: (record.contacts ?? []).map(contactToPayload),
          }
        : emptyClient,
  )

  /* The draft is reset when the sheet OPENS, not when it closes: resetting on
     close would blank the fields during the exit transition, so the sheet would
     be seen emptying itself on its way out. */
  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined

  /* A shape check only. The server owns real validation — the code reaches a
     document number, so its rule lives where the number is minted — and a
     second copy here would drift. This catches the obvious before a round
     trip. */
  const codeError =
    touched && draft.code.trim() && !/^[A-Za-z0-9][A-Za-z0-9-]*$/.test(draft.code.trim())
      ? t('directories.clients.codeInvalid')
      : undefined

  const invalid = !draft.name.trim() || Boolean(codeError)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('directories.clients.edit') : t('directories.clients.new')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('common.fields.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The code expands into a document number (FR-39, FR-68), which is why
          it is monospace and why the hint says where it shows up: somebody
          choosing one should know it will be read aloud on the phone. */}
      <Field
        label={t('directories.clients.code')}
        hint={t('directories.clients.codeHint')}
        error={codeError}
      >
        {(props) => (
          <Input
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('directories.clients.taxId')}>
          {(props) => (
            <Input
              value={draft.taxId}
              onChange={(e) => setDraft({ ...draft, taxId: e.target.value })}
              {...props}
            />
          )}
        </Field>

        <Field label={t('directories.clients.registrationId')}>
          {(props) => (
            <Input
              value={draft.registrationId}
              onChange={(e) => setDraft({ ...draft, registrationId: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </div>

      <Field label={t('common.fields.country')}>
        {(props) => (
          <Input
            value={draft.country}
            onChange={(e) => setDraft({ ...draft, country: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('directories.clients.address')}>
        {(props) => (
          <Textarea
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <ContactsEditor
        contacts={draft.contacts}
        editable={canEditContacts}
        onChange={(contacts) => setDraft({ ...draft, contacts })}
      />
    </FormSheet>
  )
}

const emptyClient: ClientDraft = {
  name: '',
  code: '',
  taxId: '',
  registrationId: '',
  country: '',
  address: '',
  contacts: [],
}

/* ── Participant ──────────────────────────────────────────────────────────── */

export interface ParticipantDraft {
  name: string
  type: ParticipantRole
  taxId: string
  registrationId: string
  country: string
  address: string
  contacts: ContactPayload[]
}

export function ParticipantFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  canEditContacts = true,
  creatableTypes,
}: {
  open: boolean
  onClose: () => void
  record?: DirectoryParticipant
  onSubmit: (values: ParticipantDraft) => void
  pending?: boolean
  canEditContacts?: boolean
  /** The types this caller may create (FR-40). A Sales user gets five of the
   *  seven — the agent types are simply not offered, because offering an option
   *  the server will refuse is worse than not offering it. */
  creatableTypes: ParticipantRole[]
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<ParticipantDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            type: record.type,
            taxId: record.taxId ?? '',
            registrationId: record.registrationId ?? '',
            country: record.country,
            address: record.address ?? '',
            contacts: (record.contacts ?? []).map(contactToPayload),
          }
        : { ...emptyParticipant, type: creatableTypes[0] ?? 'shipper' },
  )

  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined
  const invalid = !draft.name.trim()

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={
        record ? t('directories.participants.edit') : t('directories.participants.new')
      }
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('common.fields.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* Exactly one of the seven types (FR-40). The seven are distinct rather
          than a free-text label on one conflated list, which is what the source
          workbook had — a shipper and a driver shared a column, so neither
          could be filtered for. */}
      <Field label={t('directories.participants.type')} required>
        {(props) => (
          <Select
            value={draft.type}
            onChange={(type) => setDraft({ ...draft, type })}
            options={toOptions(creatableTypes, (role) => t(`participantRole.${role}`))}
            {...props}
          />
        )}
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('directories.clients.taxId')}>
          {(props) => (
            <Input
              value={draft.taxId}
              onChange={(e) => setDraft({ ...draft, taxId: e.target.value })}
              {...props}
            />
          )}
        </Field>

        <Field label={t('directories.clients.registrationId')}>
          {(props) => (
            <Input
              value={draft.registrationId}
              onChange={(e) => setDraft({ ...draft, registrationId: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* The picker, not a text field. A counterparty's country is read by
          people writing customs paperwork, and typed it arrives as
          "Kazakhstan", "Казахстан", "KZ" and "Респ. Казахстан" — four spellings
          of one fact that no report can group. The control stores the alpha-2
          code and shows the flag and the localised name.

          Anything already saved in the old free text stays selected and is
          marked as carried over (CountryDropdown handles it), so nothing that
          exists is rejected — only what is entered NEXT is constrained. */}
      <Field label={t('common.fields.country')}>
        {(props) => (
          <CountryDropdown
            value={draft.country}
            onChange={(country) => setDraft({ ...draft, country })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('directories.clients.address')}>
        {(props) => (
          <Textarea
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <ContactsEditor
        contacts={draft.contacts}
        editable={canEditContacts}
        onChange={(contacts) => setDraft({ ...draft, contacts })}
      />
    </FormSheet>
  )
}

const emptyParticipant: ParticipantDraft = {
  name: '',
  type: 'shipper',
  taxId: '',
  registrationId: '',
  country: '',
  address: '',
  contacts: [],
}

/* ── User ─────────────────────────────────────────────────────────────────── */

export interface UserDraft {
  name: string
  email: string
  role: Role
  office: Office
  language: 'en' | 'ru'
  /** Directory-ownership grants (FR-44) — additive booleans, never roles (AD-7). */
  grants: Grants
}

/** Exported so the user list renders the same three grants, in the same order,
 *  under the same labels as the form that sets them. */
export const GRANT_KEYS = [
  'manageClients',
  'manageParticipants',
  'manageTransport',
] as const

/** The two offices. A closed set rather than free text because office defaults
 *  a record's issuing entity later, and "Ashgabat " with a trailing space would
 *  silently become a third one. */
const OFFICES = ['Ashgabat', 'Yiwu'] as const

const LANGUAGES = ['ru', 'en'] as const

export function UserFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  record?: DirectoryUser
  onSubmit: (values: UserDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  // Live roles, so a role a founder created this morning is assignable this
  // afternoon (migration 0021).
  const { data: roles } = useRoles()
  const [draft, setDraft, touched, markTouched] = useFormDraft<UserDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            email: record.email,
            role: record.role,
            office: record.office,
            language: record.language,
            grants: { ...record.grants },
          }
        : emptyUser,
  )

  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined
  // A shape check, not an RFC one: the server owns real validation, and a regex
  // strict enough to be interesting rejects addresses that are actually valid.
  const emailError =
    touched && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())
      ? t('directories.users.invalidEmail')
      : undefined
  const invalid = Boolean(!draft.name.trim() || emailError) || !draft.email.trim()

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('directories.users.edit') : t('directories.users.new')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('common.fields.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* Fixed once the account exists: an address identifies an account, and
          moving one rewrites who every historical row was attributed to. The
          server does not accept a changed address on an amendment, so the field
          is read-only here rather than silently discarded there. */}
      <Field
        label={t('directories.users.email')}
        required
        error={record ? undefined : emailError}
        hint={record ? t('directories.users.emailFixed') : undefined}
      >
        {(props) => (
          <Input
            type="email"
            inputMode="email"
            value={draft.email}
            readOnly={Boolean(record)}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('directories.users.role')} required>
        {(props) => (
          <Select
            value={draft.role}
            onChange={(role) => setDraft({ ...draft, role })}
            options={(roles ?? []).map((r) => {
              const label = roleLabel(r.key, r.name)
              return {
                value: r.key,
                label: label.translated ? t(label.key) : label.text,
                hint: r.description || undefined,
              }
            })}
            {...props}
          />
        )}
      </Field>

      {/* A select, not a text field: office defaults a record's issuing entity
          later, so a typo here becomes a numbering and branding problem on
          documents nobody is looking at yet. */}
      <Field label={t('directories.users.office')} required>
        {(props) => (
          <Select
            value={draft.office}
            onChange={(office) => setDraft({ ...draft, office })}
            options={toOptions(OFFICES, (office) => office)}
            {...props}
          />
        )}
      </Field>

      {/* Preferred language lives on the account rather than in the browser:
          the daily digest is composed by a worker with no request to read a
          header from (AD-27). */}
      <Field label={t('directories.users.language')} required>
        {(props) => (
          <Select
            value={draft.language}
            onChange={(language) => setDraft({ ...draft, language })}
            options={toOptions(LANGUAGES, (code) =>
              t(code === 'ru' ? 'common.language.russian' : 'common.language.english'),
            )}
            {...props}
          />
        )}
      </Field>

      {/* Grants are additive booleans on the user record, never new roles
          (AD-7, FR-44) — which is why they are checkboxes beside the role
          rather than extra entries inside the role select. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-text-body pb-1 text-[12.5px] leading-none">
          {t('directories.users.grants')}
        </legend>
        {GRANT_KEYS.map((key) => (
          <Checkbox
            key={key}
            label={t(`directories.users.grantLabels.${key}`)}
            checked={draft.grants[key]}
            onCheckedChange={(checked) =>
              setDraft({
                ...draft,
                grants: { ...draft.grants, [key]: checked },
              })
            }
          />
        ))}
      </fieldset>

      {/* Active state is deliberately not a field here. Deactivating ends every
          session the account holds and is refused for your own account, so it
          is its own act with its own confirmation — not a checkbox that takes
          effect when somebody edits a colleague's office. */}
    </FormSheet>
  )
}

const emptyUser: UserDraft = {
  name: '',
  email: '',
  role: 'sales',
  office: 'Ashgabat',
  language: 'ru',
  grants: {
    manageClients: false,
    manageParticipants: false,
    manageTransport: false,
  },
}

/* ── Transport reference ──────────────────────────────────────────────────── */

export interface TransportDraft {
  label: string
  code: string
  kind: TransportRef['kind']
  active: boolean
}

export function TransportFormSheet({
  open,
  onClose,
  record,
  kind,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  record?: TransportRef
  /** Which group the add button belongs to — modes or equipment. */
  kind: TransportRef['kind']
  onSubmit: (values: TransportDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<TransportDraft>(
    // The kind is part of the identity: the same sheet serves both lists, and
    // switching which add button was pressed must re-seed it.
    open ? `${record?.id ?? 'new'}:${kind}` : null,
    () =>
      record
        ? {
            label: record.label,
            code: record.code,
            kind: record.kind,
            active: record.isActive,
          }
        : { ...emptyTransport, kind },
  )

  const labelError =
    touched && !draft.label.trim() ? t('common.form.required') : undefined
  const codeError = touched && !draft.code.trim() ? t('common.form.required') : undefined
  const invalid = !draft.label.trim() || !draft.code.trim()

  const heading =
    draft.kind === 'mode'
      ? t('directories.transport.mode')
      : t('directories.transport.equipment')

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={
        record
          ? t('directories.transport.editItem', { item: heading })
          : t('directories.transport.addItem', { item: heading })
      }
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('common.fields.name')} required error={labelError}>
        {(props) => (
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('directories.transport.code')}
        required
        error={codeError}
        hint={t('directories.transport.codeHint')}
      >
        {(props) => (
          <Input
            value={draft.code}
            // Codes are the machine-facing half of the pair and are always
            // upper-case, so the field enforces it rather than asking.
            onChange={(e) =>
              setDraft({ ...draft, code: e.target.value.toUpperCase() })
            }
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <Checkbox
        label={t('common.form.active')}
        checked={draft.active}
        onCheckedChange={(checked) => setDraft({ ...draft, active: checked })}
      />
    </FormSheet>
  )
}

const emptyTransport: TransportDraft = {
  label: '',
  code: '',
  kind: 'mode',
  active: true,
}

/* Re-exported so the settings surface can build its own editors on the same
   Textarea control without importing from two places. */
export { Textarea }
